import React, { useState, useEffect, useCallback } from 'react';
import { getCurrentLanguage, LANGUAGE_CHANGE_EVENT, SupportedLanguage } from '../config/languageConfig';
import { getTranslation } from '../config/translations';
import { useAddressAffordance } from '../contexts/AddressAffordanceContext';
import { useNavigationAffordance } from '../contexts/NavigationAffordanceContext';
import { useAddress } from '../contexts/AddressContext';
import { getPromotionBannerStatus, PromotionBannerStatusResponse } from '../api/promotionService';

interface BottomBannerProps {
  isVisible?: boolean;
}

const BottomBanner: React.FC<BottomBannerProps> = ({ isVisible = true }) => {
  const [shouldShow, setShouldShow] = useState<boolean>(false);
  const [currentLang, setCurrentLang] = useState<SupportedLanguage>(getCurrentLanguage());
  const [bannerStatus, setBannerStatus] = useState<PromotionBannerStatusResponse | null>(null);
  const [remainingTimeSeconds, setRemainingTimeSeconds] = useState<number | null>(null);
  
  // 어포던스 상태 감지 - 어포던스 활성화 시 배너 숨김
  const { showAddressAffordance } = useAddressAffordance();
  const { hasAnyAffordance } = useNavigationAffordance();
  const { address } = useAddress();

  // 프로모션 상태 확인
  const checkPromotionStatus = useCallback(async () => {
    try {
      const currentLang = getCurrentLanguage();
      const addressId = address?.addressId;

      if (!addressId) {
        setBannerStatus(null);
        setRemainingTimeSeconds(null);
        return false;
      }

      const response = await getPromotionBannerStatus(currentLang, addressId);
      if (response && response.bannerEligible) {
        setBannerStatus(response);
        setRemainingTimeSeconds(response.remainingTimeSeconds);
        return true;
      } else {
        setBannerStatus(null);
        setRemainingTimeSeconds(null);
        return false;
      }
    } catch (error) {
      console.error('프로모션 상태 확인 실패:', error);
      setBannerStatus(null);
      setRemainingTimeSeconds(null);
      return false;
    }
  }, [address?.addressId]);

  // 남은 시간을 배너 표시용 포맷으로 변환
  const calculateTimeLeft = useCallback((): { hours: number; minutes: number; formattedTime: string } => {
    if (remainingTimeSeconds === null || remainingTimeSeconds <= 0) {
      return { hours: 0, minutes: 0, formattedTime: '0시간 0분' };
    }

    const safeRemainingSeconds = Math.max(remainingTimeSeconds, 0);
    const hours = Math.floor(safeRemainingSeconds / 3600);
    const minutes = Math.floor((safeRemainingSeconds % 3600) / 60);
    
    return { 
      hours, 
      minutes, 
      formattedTime: `${hours}시간 ${minutes}분` 
    };
  }, [remainingTimeSeconds]);

  // 24시간 초과 시 날짜 기준으로 만료 시각 포맷
  const getExpiryDateLabel = useCallback((): string => {
    if (remainingTimeSeconds === null || remainingTimeSeconds <= 0) return '';
    const expiryDate = new Date(Date.now() + remainingTimeSeconds * 1000);

    const localeMap: Record<SupportedLanguage, string> = {
      en: 'en-US',
      ko: 'ko-KR',
      ja: 'ja-JP',
      'zh-CN': 'zh-CN',
      'zh-TW': 'zh-TW',
    };

    const locale = localeMap[currentLang] || 'en-US';
    return expiryDate.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    });
  }, [remainingTimeSeconds, currentLang]);

  // 언어 감지 및 표시 여부 결정
  const updateLanguageAndVisibility = useCallback(async () => {
    const lang = getCurrentLanguage();
    setCurrentLang(lang);
    
    // 프로모션 상태 확인
    const hasActivePromotion = await checkPromotionStatus();
    
    // 적용된 프로모션이 있을 때만 표시 (언어 제한 제거)
    if (hasActivePromotion) {
      setShouldShow(true);
    } else {
      setShouldShow(false);
    }
  }, [checkPromotionStatus]);

  // 초기 언어 감지 및 프로모션 상태 확인
  useEffect(() => {
    updateLanguageAndVisibility();
  }, [updateLanguageAndVisibility]);

  // 프로모션 데이터가 변경될 때마다 표시 여부 업데이트
  useEffect(() => {
    if (bannerStatus && bannerStatus.bannerEligible) {
      setShouldShow(true);
    } else {
      setShouldShow(false);
    }
  }, [bannerStatus]);

  // 언어 변경 이벤트 감지
  useEffect(() => {
    const handleLanguageChange = () => {
      updateLanguageAndVisibility();
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, [updateLanguageAndVisibility]);

  // 타이머 업데이트 (분 단위)
  useEffect(() => {
    if (!shouldShow || remainingTimeSeconds === null) return;

    const interval = setInterval(() => {
      setRemainingTimeSeconds((prev) => {
        if (prev === null || prev <= 60) {
          // 0에 도달하면 배너 숨김
          setShouldShow(false);
          return 0;
        }
        return prev - 60;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [shouldShow, remainingTimeSeconds]);

  // 남은 시간이 0이 될 때 보정
  useEffect(() => {
    if (remainingTimeSeconds !== null && remainingTimeSeconds <= 0) {
      setShouldShow(false);
    }
  }, [remainingTimeSeconds]);

  // 프로모션 상태 주기적 확인 (5분마다)
  useEffect(() => {
    const interval = setInterval(() => {
      checkPromotionStatus();
    }, 5 * 60 * 1000); // 5분마다 확인

    return () => clearInterval(interval);
  }, [checkPromotionStatus]);

  // 배너 텍스트 가져오기
  const getBannerText = () => {
    if (!bannerStatus) return { promotion: '', timeLeft: '' };

    const timeData = calculateTimeLeft();
    const expiryDateLabel = getExpiryDateLabel();
    const hoursText = getTranslation('promotion.banner.hours', currentLang);
    const minutesText = getTranslation('promotion.banner.minutes', currentLang);
    
    let timeString = '';
    if (timeData.hours > 0) {
      timeString = `${timeData.hours}${hoursText} ${timeData.minutes}${minutesText}`;
    } else {
      timeString = `${timeData.minutes}${minutesText}`;
    }

    const translatedMessage = bannerStatus.messageKey
      ? getTranslation(bannerStatus.messageKey, currentLang)
      : '';
    const promotionText = !translatedMessage || translatedMessage === bannerStatus.messageKey
      ? getTranslation('promotion.banner.discountApplied', currentLang)
      : translatedMessage;

    return {
      promotion: promotionText,
      timeLeft:
        remainingTimeSeconds !== null && remainingTimeSeconds > 24 * 60 * 60
          ? getTranslation('promotion.banner.expiresOn', currentLang).replace('{date}', expiryDateLabel)
          : getTranslation('promotion.banner.timeLeft', currentLang).replace('{time}', timeString)
    };
  };

  // 표시 조건 확인: 적용된 프로모션이 있고, 기타 조건들이 충족되어야 함
  if (!shouldShow || !bannerStatus || !isVisible || showAddressAffordance || hasAnyAffordance()) {
    return null;
  }

  const bannerText = getBannerText();

  return (
    <div className="fixed bottom-[70px] left-1/2 transform -translate-x-1/2 w-full max-w-mobile z-30">
      {/* 얇은 띠 형태 배너 - 좌우 색상 분할 */}
      <div className="flex items-center w-full h-10 bg-red-500 shadow-md overflow-hidden">
        {/* 좌측: 프로모션 텍스트 (빨간 배경) */}
        <div className="flex-1 min-w-0 h-full flex items-center justify-start px-4">
          <span className="text-white text-sm font-medium text-center truncate">
            {bannerText.promotion}
          </span>
        </div>
        
        {/* 우측: 타이머 (순백색 배경) */}
        <div className="shrink-0 h-full flex items-center justify-end px-3">
          <span className="bg-red-500 text-yellow-300 text-[12px] font-medium px-2 py-1 rounded text-center truncate flex items-center gap-1">
            {/* 회전하는 시계 아이콘 */}
            <svg 
              className="w-4 h-4 animate-spin" 
              style={{animationDuration: '2s'}}
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12,6 12,12 16,14"/>
            </svg>
            {bannerText.timeLeft}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BottomBanner;